/**
 * @license
 * Copyright 2018 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */

import {ENGINE} from '../engine';
import {Abs, AbsInputs, ComplexAbs, ComplexAbsInputs} from '../kernel_names';
import {Tensor} from '../tensor';
import {NamedTensorMap} from '../tensor_types';
import {convertToTensor} from '../tensor_util_env';
import {TensorLike} from '../types';

import {op} from './operation';

/**
 * Computes absolute value element-wise: `abs(x)`
 *
 * ```js
 * const x = tf.tensor1d([-1, 2, -3, 4]);
 *
 * x.abs().print();  // or tf.abs(x)
 * ```
 * @param x The input `tf.Tensor`.
 *
 * @doc {heading: 'Operations', subheading: 'Basic math'}
 */
function abs_<T extends Tensor>(x: T|TensorLike): T {
  const $x = convertToTensor(x, 'x', 'abs');

  if ($x.dtype === 'complex64') {
    const inputs: ComplexAbsInputs = {x: $x};
    return ENGINE.runKernel(ComplexAbs, inputs as {} as NamedTensorMap);
  } else {
    const inputs: AbsInputs = {x: $x};
    return ENGINE.runKernel(Abs, inputs as {} as NamedTensorMap);
  }
}

export const abs = op({abs_});
